import { Key } from 'constants/keyCodes';
import { FC, KeyboardEventHandler, ReactNode } from 'react';
import { DragDropContext, Draggable, DropResult, Droppable } from 'react-beautiful-dnd';
import { Drag16 } from 'ui/icons/aurora/16px/Drag16';
import { draggableBoxGrid, draggableBoxItem } from './DraggableBoxes.constants';
import {
  BoxRow,
  DragBox,
  DragBoxContainer,
  DragContent,
  DragGrid,
  IndexChip,
} from './DraggableBoxes.styles';

export type DraggableBoxData = {
  text: string | ReactNode;
  id: string;
  [key: string]: any;
};
type DraggableBoxConfig = Array<DraggableBoxData>;
export type DraggableBoxProps = {
  allowArrowKeyDragOnFocusedItem?: boolean;
  draggableBoxes: DraggableBoxConfig;
  isDisabled?: boolean;
  leftAlignContent?: boolean;
  onUpdate: (sourceIndex: number, destinationIndex: number) => void;
  withCounter?: boolean;
};

/** @description A draggable series of boxes indicating order and textual information.
 * The presentation and, possibly implementation, of this component is *likely* to be changed. However, the functionality
 * will remain the same.
 */
export const DraggableBoxes: FC<DraggableBoxProps> = ({
  allowArrowKeyDragOnFocusedItem = false,
  draggableBoxes,
  isDisabled = false,
  leftAlignContent = true,
  onUpdate,
  withCounter = true,
}) => {
  function onDragEnd(result: DropResult) {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;
    onUpdate(result.source.index, result.destination.index);
  }

  const boxElements = draggableBoxes.map(({ text, id }: DraggableBoxData, index: number) => {
    /**
     * Custom handler for keyboard events on the focused box element.
     * @note react-beautiful-dnd has built-in keyboard drag functionality, but it requires pressing space bar to engage it. This custom handler is set up to allow keyboard drag using arrow buttons on the focused item without needing to press space bar.
     */
    const handleKeyDown: KeyboardEventHandler = (e) => {
      switch (e.key) {
        case Key.ARROW_UP:
        case Key.ARROW_LEFT:
          e.preventDefault();
          if (index > 0) onUpdate(index, index - 1);
          break;
        case Key.ARROW_DOWN:
        case Key.ARROW_RIGHT:
          e.preventDefault();
          if (index < draggableBoxes.length - 1) onUpdate(index, index + 1);
          break;
        default:
      }
    };

    return (
      <Draggable draggableId={`${id}`} index={index} key={`${id}`} isDragDisabled={isDisabled}>
        {(draggableProvided) => (
          <BoxRow
            id={`draggable-${id}`}
            ref={draggableProvided.innerRef}
            {...draggableProvided.draggableProps}
            {...draggableProvided.dragHandleProps}
            key={id}
            data-test={draggableBoxItem}
            withCounter={withCounter}
            onKeyDown={allowArrowKeyDragOnFocusedItem ? handleKeyDown : undefined}
          >
            {withCounter && <IndexChip>{index + 1}</IndexChip>}
            <DragBoxContainer>
              <DragBox isDisabled={isDisabled} leftAlignContent={leftAlignContent}>
                {leftAlignContent && <DragContent>{text}</DragContent>}
                <Drag16 />
                {!leftAlignContent && <DragContent>{text}</DragContent>}
              </DragBox>
            </DragBoxContainer>
          </BoxRow>
        )}
      </Draggable>
    );
  });

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="list" isDropDisabled={isDisabled}>
        {(provided) => (
          <div ref={provided.innerRef} {...provided.droppableProps}>
            <DragGrid data-test={draggableBoxGrid}>{boxElements}</DragGrid>
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};
